import React, { useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import BrowserCategory from "./ui/BrowserCategory"

/**
 * Main Help Center Browser Component
 * Renders the provided TOC data without making its own API calls
 * With added status filtering to only show published content
 */
const HelpCenterBrowser = ({
  rootCategoriesData = null,
  onArticleSelect,
  onCategorySelect,
  currentPath = {},
  expandedCategories = {},
}) => {
  const texts = TextBundle({ id: "help-center" })

  // Effect to handle resetting expanded categories when on home page
  useEffect(() => {
    // Check if currentPath is empty (all values are null)
    const isPathEmpty =
      !currentPath.type &&
      !currentPath.slug &&
      !currentPath.categoryPath &&
      !currentPath.exactPath &&
      !currentPath.directParentPath
    // If path is empty but we have expanded categories, notify parent to reset them
    if (
      isPathEmpty &&
      expandedCategories &&
      Object.keys(expandedCategories).length > 0
    ) {
      onCategorySelect && onCategorySelect(null, true) // Second param indicates reset request
    }
  }, [currentPath, expandedCategories, onCategorySelect])

  // Display error if no rootCategoriesData is provided
  if (!rootCategoriesData) {
    return (
      <Typography variant="body1">
        {texts?.no_categories_found || "No help center categories found"}
      </Typography>
    )
  }

  /**
   * Process the rootCategoriesData to determine what to render
   * Only include published categories
   */
  const getCategoriesToRender = () => {
    let categories = []

    // Check if rootCategoriesData is an array already
    if (Array.isArray(rootCategoriesData)) {
      categories = rootCategoriesData
    }
    // Check if it has the content.categories structure (standard TOC structure)
    else if (
      rootCategoriesData.content &&
      Array.isArray(rootCategoriesData.content.categories)
    ) {
      categories = rootCategoriesData.content.categories
    }
    // Check if it's the full API response with a toc wrapper
    else if (
      rootCategoriesData.toc &&
      rootCategoriesData.toc.content &&
      Array.isArray(rootCategoriesData.toc.content.categories)
    ) {
      categories = rootCategoriesData.toc.content.categories
    }
    // If it's another structure we don't recognize
    else {
      console.error(
        "Unrecognized rootCategoriesData structure:",
        rootCategoriesData
      )
      return []
    }

    // Filter out any categories with draft status
    return categories.filter((category) => {
      // Check both possible status locations based on data structure
      const status = category.status || category.content?.status
      return !status || status === "published"
    })
  }

  const categoriesToRender = getCategoriesToRender()

  /**
   * If we have an empty array after processing, show an error
   */
  if (categoriesToRender.length === 0) {
    return (
      <Typography variant="error">
        {texts?.invalid_cat_structure ||
          "Error: Invalid category data structure or no categories found"}
      </Typography>
    )
  }

  /**
   * Render the root categories
   */
  return (
    <div>
      {categoriesToRender.map((category) => (
        <BrowserCategory
          key={category._id}
          category={category}
          onArticleSelect={onArticleSelect}
          onCategorySelect={onCategorySelect}
          expandedCategories={expandedCategories}
          currentPath={currentPath}
          fullPath=""
          // Pass TOC info to avoid additional API calls
          preloadedData={true}
        />
      ))}
    </div>
  )
}

export default HelpCenterBrowser
